import React from 'react';

const CouncillorsTab = ({ 
  councillorClaimsStatus, 
  pendingBlasts, 
  errorReports,
  sendingClaimLink,
  sendClaimLink,
  setCouncillorForm,
  setShowCouncillorModal,
  reviewBlast,
  onImitateCouncillor
}) => {
  return (
    <div className="space-y-8">
      {/* Councillor Accounts & Claim Status */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Councillor Accounts</h2>
            <p className="text-gray-500 text-sm mt-1">
              Send claim links so councillors can access their dashboards. 
              <span className="text-green-400 ml-2">{councillorClaimsStatus.filter(c => c.is_claimed).length} claimed</span>
              <span className="text-gray-600 ml-2">/ {councillorClaimsStatus.length} total</span>
            </p>
          </div>
          <button
            onClick={() => { setShowCouncillorModal(true); setCouncillorForm({ id: '', name: '', match_name: '', ward: '', email: '', phone: '', bio: '', image_url: '', postal_codes: '', emails_delivered: 0, emails_responded: 0, emails_redemption: 0 }); }}
            className="bg-green-500 hover:bg-green-600 text-black font-bold py-2 px-6 rounded-sm uppercase tracking-wider"
          >
            + Add Councillor
          </button>
        </div>

        {/* Claim Status Table */}
        <div className="bg-gray-900 border border-gray-800 rounded overflow-hidden mb-6">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800">
                <th className="px-4 py-3 text-left font-mono text-xs uppercase text-gray-400">Councillor</th>
                <th className="px-4 py-3 text-left font-mono text-xs uppercase text-gray-400">Ward</th>
                <th className="px-4 py-3 text-left font-mono text-xs uppercase text-gray-400">Email</th>
                <th className="px-4 py-3 text-left font-mono text-xs uppercase text-gray-400">Status</th>
                <th className="px-4 py-3 text-right font-mono text-xs uppercase text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {councillorClaimsStatus.map((c) => (
                <tr key={c.id} className="border-t border-gray-800 hover:bg-gray-850">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.image_url ? (
                        <img src={c.image_url} alt={c.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-xs text-gray-400">{c.name?.[0]}</div>
                      )}
                      <span className="text-white font-mono text-sm">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{c.ward}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.email || <span className="text-red-500">No email set</span>}</td>
                  <td className="px-4 py-3">
                    {c.is_claimed ? (
                      <span className="px-2 py-1 bg-green-900/30 border border-green-800 text-green-400 text-xs font-mono rounded">
                        ✓ CLAIMED
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-900/30 border border-yellow-800 text-yellow-500 text-xs font-mono rounded">
                        UNCLAIMED
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => onImitateCouncillor(c.id)}
                        className="px-3 py-1 bg-[#C5A52A] hover:bg-[#D4B93A] text-black text-xs font-mono font-bold rounded transition-colors"
                        data-testid={`imitate-councillor-${c.id}`}
                      >
                        IMITATE
                      </button>
                      <button
                        onClick={() => sendClaimLink(c.id)}
                        disabled={!c.email || sendingClaimLink === c.id}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-mono rounded transition-colors"
                        title={!c.email ? 'Add email first' : 'Send magic link to claim dashboard'}
                        data-testid={`send-claim-link-${c.id}`}
                      >
                        {sendingClaimLink === c.id ? 'Sending...' : c.is_claimed ? 'Re-send Link' : 'Send Claim Link'}
                      </button>
                      <button
                        onClick={() => {
                          setCouncillorForm({ ...c, emails_delivered: c.emails_delivered || 0, emails_responded: c.emails_responded || 0, emails_redemption: c.emails_redemption || 0 });
                          setShowCouncillorModal(true);
                        }}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs font-mono rounded transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Email Blasts Review */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          Email Blasts Pending Review
          {pendingBlasts.length > 0 && (
            <span className="bg-yellow-500 text-black text-xs font-mono px-2 py-0.5 rounded-full">{pendingBlasts.length}</span>
          )}
        </h3>
        {pendingBlasts.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 p-6 rounded text-center text-gray-500 text-sm font-mono">
            No pending email blasts to review
          </div>
        ) : (
          <div className="space-y-4">
            {pendingBlasts.map((blast) => (
              <div key={blast.id} className="bg-gray-900 border border-yellow-800/40 p-6 rounded">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-white font-bold">{blast.subject}</h4>
                    <p className="text-gray-500 text-xs font-mono mt-1">
                      {blast.councillor_name} • {blast.councillor_ward} • {new Date(blast.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-yellow-900/30 border border-yellow-700 text-yellow-400 text-xs font-mono rounded">
                    PENDING REVIEW
                  </span>
                </div>
                <div className="bg-gray-950 p-4 rounded mb-4 text-gray-300 text-sm max-h-32 overflow-y-auto">
                  {blast.body}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => reviewBlast(blast.id, 'approved')}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-mono uppercase rounded"
                    data-testid={`approve-blast-${blast.id}`}
                  >
                    Approve & Send
                  </button>
                  <button
                    onClick={() => {
                      const notes = window.prompt('Rejection reason (optional):') || '';
                      reviewBlast(blast.id, 'rejected', notes);
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-mono uppercase rounded"
                    data-testid={`reject-blast-${blast.id}`}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Councillor Error Reports */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">
          Data Correction Requests
          {errorReports.length > 0 && (
            <span className="ml-2 bg-red-500/20 text-red-400 text-xs font-mono px-2 py-0.5 rounded-full border border-red-800">{errorReports.length}</span>
          )}
        </h3>
        {errorReports.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 p-6 rounded text-center text-gray-500 text-sm font-mono">
            No error reports from councillors
          </div>
        ) : (
          <div className="space-y-3">
            {errorReports.map((report) => (
              <div key={report.id} className="bg-gray-900 border border-gray-800 p-4 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-mono text-sm">{report.field_name}: <span className="text-red-400">{report.reported_value}</span> → <span className="text-green-400">{report.correct_value}</span></p>
                    <p className="text-gray-500 text-xs mt-1">{report.notes}</p>
                  </div>
                  <span className="text-xs font-mono text-gray-600">{new Date(report.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CouncillorsTab;
